import React from 'react';
import { emptyStateIsTrustworthy, type ScopeStatus } from '@/lib/tenantScope';

/**
 * SUB-4.10 · D-6. NFR8's SECOND sentence, which had been shipped without a
 * consumer since SUB-3.12.
 *
 * > …and the system never silently returns a subset of what they believe they
 * > are looking at.
 *
 * An empty list is the purest form of that subset. "No projects found" is a
 * claim about the DATA, and a list served under a scope nobody has confirmed
 * cannot make it: the emptiness may be the scope. `emptyStateIsTrustworthy` was
 * written for exactly this and was called by nothing but its own unit test.
 *
 * ⚠️ THE CASE THIS ACTUALLY COVERS IS `unknown`, AND THAT IS NOT THE WEAK ONE.
 * Under `narrowed`, `degraded` and `unresolved` a blocking notice is already
 * over the page, so nobody is reading the list underneath. `unknown` raises no
 * notice ON PURPOSE, because doing so on every flaky request would train the
 * user to dismiss the notice that matters. So `unknown` is the one state where
 * a wrong empty state is both possible and unaccompanied, and it is precisely
 * the state a three-states-two-values reader collapses into `ok`.
 *
 * ⚠️ IT NEVER HIDES THE ORIGINAL MESSAGE, it qualifies it. A user whose list is
 * genuinely empty and whose scope merely has not been confirmed yet still needs
 * to know there is nothing to act on; what they must not be told is that the
 * emptiness is a fact about their account.
 */
export function ScopeAwareEmptyState({
  scopeStatus,
  className,
  children,
}: {
  scopeStatus: ScopeStatus;
  className?: string;
  children: React.ReactNode;
}) {
  const trustworthy = emptyStateIsTrustworthy(scopeStatus);

  return (
    <div className={className}>
      <div>{children}</div>
      {!trustworthy && (
        <p className="mt-2 text-xs">
          This has not been confirmed against your current account yet, so an empty result
          here is not a statement that there is nothing.
        </p>
      )}
    </div>
  );
}

export default ScopeAwareEmptyState;
